import React, { useEffect, useState, useRef } from "react";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import resolveImageUrl from "../../utils/imageUtils";
import * as XLSX from "xlsx";
import { exportClickTrackingData, deleteClickTrackingData, exportSupervisorClickTrackingData, deleteSupervisorClickTrackingData } from "../../utils/clickTracking";

const RAW_CLICK_HEADERS = [
  "Timestamp (UTC)",
  "Page URL",
  "Inspector ID",
  "Inspector Name",
  "Inspector Username",
  "Supervisor ID",
  "Supervisor Name",
  "Supervisor Username",
  "Property ID",
  "Browser",
  "IP Address",
  "User Agent",
  "Timestamp (Local)",
  "Element Clicked",
  "Old Value",
  "New Value",
  "Searched PIN",
  "Comment",
  "Comment Text",
  "Inspector Text",
  "Feature Changes",
  "Overall Explanation",
  "Star Action",
];

const RAW_CLICK_COL_WIDTHS = [
  { wch: 22 },
  { wch: 40 },
  { wch: 14 },
  { wch: 20 },
  { wch: 25 },
  { wch: 14 },
  { wch: 20 },
  { wch: 25 },
  { wch: 18 },
  { wch: 12 },
  { wch: 16 },
  { wch: 60 },
  { wch: 22 },
  { wch: 45 },
  { wch: 30 },
  { wch: 30 },
  { wch: 25 },
  { wch: 80 },
  { wch: 80 },
  { wch: 80 },
  { wch: 80 },
  { wch: 120 },
  { wch: 15 },
];

const mapRawClickRow = (click) => {
  const timestampLocal = click.timestamp_local || new Date(click.timestamp).toLocaleString("en-US", { timeZone: "Asia/Karachi" });

  let explanationText = "";
  switch (click.element_clicked) {
    case "Overall Explanation":
      explanationText = click.overall_explanation;
      break;
    case "Number of Floors Explanation":
      explanationText = click.floor_explanation;
      break;
    case "Land Use Explanation":
      explanationText = click.landuse_explanation;
      break;
    case "Land Area Explanation":
      explanationText = click.landarea_explanation;
      break;
    case "Covered Area Explanation":
      explanationText = click.coveredarea_explanation;
      break;
  }

  const commentValue = click.comment ? `${click.comment}` : "";
  const commentTextValue = click.comment_text ? `${click.comment_text}` : "";
  const inspectorTextValue = click.inspector_text ? `${click.inspector_text}` : "";
  const featureChangesValue = click.feature_changes ? `${click.feature_changes}` : "";

  return [
    click.timestamp,
    click.page_url,
    click.inspector_id,
    click.inspector_name,
    click.inspector_username,
    click.supervisor_id,
    click.supervisor_name,
    click.supervisor_username,
    click.property_id,
    click.browser,
    click.ip_address,
    click.user_agent,
    timestampLocal,
    click.element_clicked,
    click.old_value ? `${click.old_value} @ ${timestampLocal}` : "",
    click.new_value ? `${click.new_value} @ ${timestampLocal}` : "",
    click.searched_pin ? `${click.searched_pin} @ ${timestampLocal}` : "",
    commentValue,
    commentTextValue,
    inspectorTextValue,
    featureChangesValue,
    explanationText ? `${explanationText} @ ${timestampLocal}` : "",
    click.star_action ? `${click.star_action} @ ${timestampLocal}` : "",
  ];
};

const SUPERVISOR_RAW_CLICK_HEADERS = [
  "Timestamp (UTC)",
  "Page URL",
  "Supervisor ID",
  "Supervisor Name",
  "Supervisor Username",
  "Property ID",
  "Browser",
  "IP Address",
  "User Agent",
  "Timestamp (Local)",
  "Element Clicked",
  "Old Value",
  "New Value",
  "Searched PIN",
  "Comment",
  "Comment Text",
  "Inspector Text",
  "Feature Changes",
  "Overall Explanation",
  "Star Action",
];

const SUPERVISOR_RAW_CLICK_COL_WIDTHS = [
  { wch: 22 },
  { wch: 40 },
  { wch: 14 },
  { wch: 20 },
  { wch: 25 },
  { wch: 18 },
  { wch: 12 },
  { wch: 16 },
  { wch: 60 },
  { wch: 22 },
  { wch: 45 },
  { wch: 30 },
  { wch: 30 },
  { wch: 25 },
  { wch: 80 },
  { wch: 80 },
  { wch: 80 },
  { wch: 80 },
  { wch: 120 },
  { wch: 15 },
];

const mapSupervisorRawClickRow = (click) => {
  const timestampLocal = click.timestamp_local || new Date(click.timestamp).toLocaleString("en-US", { timeZone: "Asia/Karachi" });

  let explanationText = "";
  switch (click.element_clicked) {
    case "Overall Explanation":
      explanationText = click.overall_explanation;
      break;
    case "Number of Floors Explanation":
      explanationText = click.floor_explanation;
      break;
    case "Land Use Explanation":
      explanationText = click.landuse_explanation;
      break;
    case "Land Area Explanation":
      explanationText = click.landarea_explanation;
      break;
    case "Covered Area Explanation":
      explanationText = click.coveredarea_explanation;
      break;
  }

  const commentValue = click.comment ? `${click.comment}` : "";
  const commentTextValue = click.comment_text ? `${click.comment_text}` : "";
  const inspectorTextValue = click.inspector_text ? `${click.inspector_text}` : "";
  const featureChangesValue = click.feature_changes ? `${click.feature_changes}` : "";

  return [
    click.timestamp,
    click.page_url,
    click.supervisor_id,
    click.supervisor_name,
    click.supervisor_username,
    click.property_id,
    click.browser,
    click.ip_address,
    click.user_agent,
    timestampLocal,
    click.element_clicked,
    click.old_value ? `${click.old_value} @ ${timestampLocal}` : "",
    click.new_value ? `${click.new_value} @ ${timestampLocal}` : "",
    click.searched_pin ? `${click.searched_pin} @ ${timestampLocal}` : "",
    commentValue,
    commentTextValue,
    inspectorTextValue,
    featureChangesValue,
    explanationText ? `${explanationText} @ ${timestampLocal}` : "",
    click.star_action ? `${click.star_action} @ ${timestampLocal}` : "",
  ];
};

// ─── Field config ──────────────────────────────────────────────────────────────
const EDITABLE_FIELDS = [
  { key: "circle", label: "Circle", type: "text" },
  { key: "occupationstatus", label: "Occupation Status", type: "text" },
  { key: "refno", label: "Ref No", type: "text" },
  { key: "location", label: "Location", type: "text" },
  { key: "floor_admin", label: "Floors (Admin)", type: "text" },
  { key: "landuse_admin", label: "Land Use (Admin)", type: "text" },
  { key: "landarea_admin", label: "Land Area (Admin)", type: "text" },
  { key: "coveredarea_admin", label: "Covered Area (Admin)", type: "text" },
  { key: "floors_pred", label: "Floors (Predicted)", type: "text" },
  { key: "land_use_pred", label: "Land Use (Predicted)", type: "text" },
  { key: "coveredarea_combined_pred", label: "Covered Area (Pred)", type: "text" },
  { key: "landarea_combined_pred", label: "Land Area (Pred)", type: "text" },
  { key: "floors_ml", label: "Floors (ML)", type: "text" },
  { key: "landuse_ml", label: "Land Use (ML)", type: "text" },
  { key: "landarea_ml", label: "Land Area (ML)", type: "text" },
  { key: "coveredarea_ml", label: "Covered Area (ML)", type: "text" },
  { key: "exp_garv", label: "Expected GARV", type: "text" },
  { key: "admin_garv", label: "Admin GARV", type: "text" },
  { key: "reassess", label: "Reassess", type: "text" },
];

const getImageUrl = (imagePath) => resolveImageUrl(imagePath) || null;

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

const getExifOrientation = (arrayBuffer) => {
  try {
    const view = new DataView(arrayBuffer);
    if (view.byteLength < 4) return null;
    if (view.getUint16(0, false) !== 0xffd8) return null;

    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      const marker = view.getUint16(offset, false);
      offset += 2;

      if ((marker & 0xff00) !== 0xff00) return null;
      if (marker === 0xffd9) return null;

      const length = view.getUint16(offset, false);
      if (length < 2) return null;

      if (marker === 0xffe1) {
        const exifStart = offset + 2;
        if (exifStart + 10 > view.byteLength) return null;
        if (view.getUint32(exifStart, false) !== 0x45786966) return null;

        const tiffStart = exifStart + 6;
        if (tiffStart + 8 > view.byteLength) return null;

        const endian = view.getUint16(tiffStart, false);
        const little = endian === 0x4949;
        if (!little && endian !== 0x4d4d) return null;

        if (view.getUint16(tiffStart + 2, little) !== 0x002a) return null;
        const ifdOffset = view.getUint32(tiffStart + 4, little);
        const ifdStart = tiffStart + ifdOffset;
        if (ifdStart + 2 > view.byteLength) return null;

        const entries = view.getUint16(ifdStart, little);
        for (let i = 0; i < entries; i++) {
          const entryStart = ifdStart + 2 + i * 12;
          if (entryStart + 12 > view.byteLength) break;

          const tag = view.getUint16(entryStart, little);
          if (tag !== 0x0112) continue;

          const type = view.getUint16(entryStart + 2, little);
          const count = view.getUint32(entryStart + 4, little);
          if (type !== 3 || count !== 1) return null;

          const value = view.getUint16(entryStart + 8, little);
          return value || null;
        }
        return null;
      }

      offset += length;
    }
    return null;
  } catch {
    return null;
  }
};

const getImageDimensionsFromBlob = async (blob) => {
  try {
    if (typeof createImageBitmap === "function") {
      const bitmap = await createImageBitmap(blob);
      const dims = { width: bitmap.width, height: bitmap.height };
      bitmap.close?.();
      return dims;
    }
  } catch (e) {
    void e;
  }

  return await new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: null, height: null });
    };
    img.src = url;
  });
};

// ─── Edit Modal ────────────────────────────────────────────────────────────────
function EditModal({ property, inspectorId, onClose, onSaved }) {
  const [form, setForm] = useState({ ...property });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    property.image ? getImageUrl(property.image) : null
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const handleChange = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      EDITABLE_FIELDS.forEach(({ key }) => {
        fd.append(key, form[key] ?? "");
      });
      if (inspectorId) {
        fd.append("userId", inspectorId);
      }
      if (imageFile) fd.append("image", imageFile);

      const response = await axios.put(`/properties/data/${property.pin}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Use the updated property from the server response
      if (response.data.property) {
        onSaved(response.data.property);
      } else {
        onSaved({ ...form, image: imagePreview });
      }
      onClose();
    } catch (err) {
      console.error("Save error:", err);
      setError(err.response?.data?.error || err.message || "Failed to save property.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-blue-700">
          <div>
            <h2 className="text-xl font-bold text-white">Edit Property</h2>
            <p className="text-blue-200 text-sm mt-0.5">PIN: {property.pin}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-2xl font-light transition"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Image */}
          <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Property Image
            </label>
            <div className="flex items-start gap-4">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Property"
                  style={{ imageOrientation: "from-image" }}
                  className="w-32 h-32 object-cover rounded-xl border border-gray-200 shadow"
                />
              ) : (
                <div className="w-32 h-32 bg-gray-200 rounded-xl flex items-center justify-center text-gray-400 text-sm">
                  No Image
                </div>
              )}
              <div>
                <button
                  onClick={() => fileRef.current.click()}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                >
                  {imagePreview ? "Change Image" : "Upload Image"}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Portrait orientation recommended
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </div>
            </div>
          </div>

          {/* Fields grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {EDITABLE_FIELDS.map(({ key, label, type }) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {label}
                </label>
                <input
                  type={type}
                  value={form[key] ?? ""}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-gray-50 hover:bg-white"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition text-sm font-semibold shadow"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ────────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-xl">
            ⚠️
          </div>
          <h3 className="text-lg font-bold text-gray-900">Confirm Action</h3>
        </div>
        <p className="text-gray-600 text-sm mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-5 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-semibold"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function ManageAssignments() {
  const currentUser = JSON.parse(localStorage.getItem("user")) || {};
  // Users
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rawExportStage, setRawExportStage] = useState('');
  const rawExportRunning = useRef(false);
  const [roleFilter, setRoleFilter] = useState("inspector"); // "inspector" | "supervisor"
  const [searchUser, setSearchUser] = useState("");

  // Properties
  const [assignedProperties, setAssignedProperties] = useState([]);
  const [uploadedProperties, setUploadedProperties] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [propsError, setPropsError] = useState("");
  const [supervisorPropertyTab, setSupervisorPropertyTab] = useState("assigned"); // "assigned" | "uploaded"

  // Selection
  const [selectedPins, setSelectedPins] = useState([]);
  const [selectedUploadedIds, setSelectedUploadedIds] = useState([]);

  // Modals
  const [editingProperty, setEditingProperty] = useState(null);
  const [confirm, setConfirm] = useState(null); // { message, onConfirm }

  // Search / filter within properties
  const [propSearch, setPropSearch] = useState("");
  const [imageFilter, setImageFilter] = useState("all");

  const [rotateState, setRotateState] = useState({
    running: false,
    total: 0,
    processed: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
    currentPin: "",
  });

  // ── Load all users (inspectors + supervisors) ──────────────────────────────
  const [usersError, setUsersError] = useState("");

  useEffect(() => {
    axios
      .get("/users/all")
      .then((res) => {
        console.log("Users loaded:", res.data);
        setUsers(res.data);
        setUsersError("");
      })
      .catch((err) => {
        console.error("Failed to load users:", err);
        setUsersError(err.response?.data?.error || "Failed to load users.");
        setUsers([]);
      });
  }, []);

  // ─── Load properties when user selected ────────────────────────────────────
  useEffect(() => {
    if (!selectedUser) {
      setAssignedProperties([]);
      setUploadedProperties([]);
      setProperties([]);
      setSelectedPins([]);
      setSelectedUploadedIds([]);
      setPropSearch("");
      return;
    }
    setLoadingProps(true);
    setPropsError("");
    setSelectedPins([]);
    setSelectedUploadedIds([]);

    const loadSupervisorData = async () => {
      try {
        const [assignedRes, uploadedRes] = await Promise.all([
          axios.get(`/properties/assignments/supervisor/${selectedUser.id}`),
          axios.get(`/supervisor-properties/all/${selectedUser.id}`)
        ]);
        setAssignedProperties(Array.isArray(assignedRes.data) ? assignedRes.data : []);
        setUploadedProperties(Array.isArray(uploadedRes.data) ? uploadedRes.data : []);
      } catch {
        setPropsError("Failed to load properties for this user.");
      } finally {
        setLoadingProps(false);
      }
    };

    const loadInspectorData = async () => {
      try {
        const res = await axios.get(`/properties/assignments/inspector/${selectedUser.id}?includeAll=true`);
        setAssignedProperties(Array.isArray(res.data) ? res.data : []);
        setUploadedProperties([]);
      } catch {
        setPropsError("Failed to load properties for this user.");
      } finally {
        setLoadingProps(false);
      }
    };

    if (selectedUser.role === "supervisor") {
      loadSupervisorData();
    } else {
      loadInspectorData();
    }
  }, [selectedUser]);

  // Update current properties based on tab
  useEffect(() => {
    if (selectedUser?.role === "supervisor") {
      setProperties(supervisorPropertyTab === "assigned" ? assignedProperties : uploadedProperties);
    } else {
      setProperties(assignedProperties);
    }
  }, [supervisorPropertyTab, assignedProperties, uploadedProperties, selectedUser]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const filteredUsers = users.filter((u) => {
    const matchRole =
      u.role === roleFilter;
    const matchSearch =
      !searchUser ||
      u.name?.toLowerCase().includes(searchUser.toLowerCase()) ||
      u.username?.toLowerCase().includes(searchUser.toLowerCase()) ||
      u.circle?.toLowerCase().includes(searchUser.toLowerCase());
    return matchRole && matchSearch;
  });

  const filteredProperties = properties.filter((p) => {
    let matches = true;

    if (propSearch) {
      const q = propSearch.toLowerCase();
      matches =
        String(p.pin).toLowerCase().includes(q) ||
        String(p.circle || "").toLowerCase().includes(q) ||
        String(p.location || "").toLowerCase().includes(q) ||
        String(p.occupationstatus || "").toLowerCase().includes(q);
    }

    if (imageFilter === "has-image") {
      matches = matches && !!p.image;
    } else if (imageFilter === "no-image") {
      matches = matches && !p.image;
    }

    return matches;
  });

  const togglePin = (pin) =>
    setSelectedPins((prev) =>
      prev.includes(pin) ? prev.filter((p) => p !== pin) : [...prev, pin]);

  const toggleAll = () => {
    if (selectedPins.length === filteredProperties.length) {
      setSelectedPins([]);
    } else {
      setSelectedPins(filteredProperties.map((p) => p.pin));
    }
  };

  const toggleUploadedId = (id) =>
    setSelectedUploadedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);

  const toggleAllUploaded = () => {
    if (selectedUploadedIds.length === filteredProperties.length) {
      setSelectedUploadedIds([]);
    } else {
      setSelectedUploadedIds(filteredProperties.map((p) => p.id));
    }
  };

  // ── Delete selected properties ────────────────────────────────────────────
  const handleDeleteSelected = () => {
    if (!selectedPins.length) return;
    setConfirm({
      message: `Delete ${selectedPins.length} selected assignment(s) for "${selectedUser?.name}"? This cannot be undone.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          if (selectedUser.role === "supervisor") {
            await axios.delete("/properties/assignments/bulk-delete-supervisor", {
              data: { supervisorId: selectedUser.id, pins: selectedPins },
            });
          } else {
            await axios.delete("/properties/assignments/bulk-delete", {
              data: { inspectorId: selectedUser.id, pins: selectedPins },
            });
          }
          setProperties((prev) =>
            prev.filter((p) => !selectedPins.includes(p.pin))
          );
          setSelectedPins([]);
        } catch (err) {
          alert(err.response?.data?.error || "Failed to delete assignments.");
        }
      },
    });
  };

  // ─── Delete selected uploaded properties ────────────────────────────────────
  const handleDeleteSelectedUploaded = () => {
    if (!selectedUploadedIds.length) return;
    setConfirm({
      message: `Delete ${selectedUploadedIds.length} uploaded propert(y) for "${selectedUser?.name}"? This cannot be undone.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await axios.delete("/supervisor-properties/bulk-delete", {
            data: { propertyIds: selectedUploadedIds, supervisorId: selectedUser.id },
          });
          setUploadedProperties(prev => prev.filter(p => !selectedUploadedIds.includes(p.id)));
          setSelectedUploadedIds([]);
        } catch (err) {
          alert(err.response?.data?.error || "Failed to delete properties.");
        }
      },
    });
  };

  // ─── Delete all uploaded properties ─────────────────────────────────────────
  const handleDeleteAllUploaded = () => {
    if (!selectedUser || !uploadedProperties.length) return;
    setConfirm({
      message: `Delete ALL ${uploadedProperties.length} uploaded properties for "${selectedUser?.name}"? This cannot be undone.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await axios.delete("/supervisor-properties/delete-all", {
            data: { supervisorId: selectedUser.id },
          });
          setUploadedProperties([]);
          setSelectedUploadedIds([]);
        } catch (err) {
          alert(err.response?.data?.error || "Failed to delete all properties.");
        }
      },
    });
  };

  // ─── Delete a single uploaded property ─────────────────────────────────────
  const handleDeleteSingleUploaded = (property) => {
    setConfirm({
      message: `Delete uploaded property with PIN "${property.pin}"? This cannot be undone.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await axios.delete(`/supervisor-properties/${property.id}`);
          setUploadedProperties(prev => prev.filter(p => p.id !== property.id));
        } catch (err) {
          alert(err.response?.data?.error || "Failed to delete property.");
        }
      },
    });
  };

  // ── Delete ALL properties of user ─────────────────────────────────────────
  const handleDeleteAll = () => {
    if (!selectedUser) return;
    setConfirm({
      message: `Remove ALL ${properties.length} assignment(s) for "${selectedUser?.name}" (${selectedUser?.username})? This cannot be undone.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          if (selectedUser.role === "supervisor") {
            await axios.delete("/properties/assignments/by-supervisor", {
              data: { username: selectedUser.username },
            });
          } else {
            await axios.delete("/properties/assignments/by-inspector", {
              data: { username: selectedUser.username },
            });
          }
          setProperties([]);
          setSelectedPins([]);
        } catch (err) {
          alert(err.response?.data?.error || "Failed to delete all assignments.");
        }
      },
    });
  };

  // Delete ALL properties from the system (admin only)
  const handleDeleteAllProperties = () => {
    setConfirm({
      message: "WARNING: Are you 100% sure you want to DELETE ALL PROPERTIES from the system? This action cannot be undone!",
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await axios.delete("/properties/all");
          alert(`Successfully deleted ${res.data.deletedCount} properties!`);
          setAssignedProperties([]);
          setUploadedProperties([]);
          setProperties([]);
          setSelectedPins([]);
          setSelectedUploadedIds([]);
        } catch (err) {
          alert(err.response?.data?.error || "Failed to delete all properties.");
        }
      },
    });
  };

  // ── After edit saved ──────────────────────────────────────────────────────
  const handlePropertySaved = (updated) => {
    setProperties((prev) =>
      prev.map((p) => (p.pin === updated.pin ? { ...p, ...updated } : p))
    );
  };

  const handleRotateImages = async () => {
    if (!selectedUser) return;

    const targets = filteredProperties.filter((p) => !!p.image);
    if (!targets.length) {
      alert("No images found in the list.");
      return;
    }

    const confirmMsg = `Rotate/normalize ${targets.length} image(s) for "${selectedUser.name}"?`;
    if (!window.confirm(confirmMsg)) return;

    setRotateState({
      running: true,
      total: targets.length,
      processed: 0,
      updated: 0,
      skipped: 0,
      failed: 0,
      currentPin: "",
    });

    for (let index = 0; index < targets.length; index++) {
      const p = targets[index];
      setRotateState((prev) => ({
        ...prev,
        processed: index,
        currentPin: p.pin,
      }));

      try {
        const rawUrl = getImageUrl(p.image);
        if (!rawUrl) {
          setRotateState((prev) => ({ ...prev, skipped: prev.skipped + 1 }));
          continue;
        }

        const res = await fetch(appendCacheBuster(rawUrl, Date.now()), {
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`Failed to download image (${res.status})`);

        const blob = await res.blob();
        const [arrayBuffer, dims] = await Promise.all([
          blob.arrayBuffer(),
          getImageDimensionsFromBlob(blob),
        ]);

        const orientation = getExifOrientation(arrayBuffer);
        const needsFix =
          (orientation && orientation !== 1) ||
          (dims.width && dims.height && dims.width > dims.height);

        if (!needsFix) {
          setRotateState((prev) => ({ ...prev, skipped: prev.skipped + 1 }));
          continue;
        }

        const fileType = blob.type || "image/jpeg";
        const ext =
          (fileType.split("/")[1] || "jpg").toLowerCase().replace("jpeg", "jpg");
        const file = new File([blob], `pin_${p.pin}.${ext}`, { type: fileType });

        const fd = new FormData();
        EDITABLE_FIELDS.forEach(({ key }) => {
          fd.append(key, p[key] ?? "");
        });
        if (currentUser?.id) fd.append("userId", String(currentUser.id));
        fd.append("image", file);

        const response = await axios.put(`/properties/data/${p.pin}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const updatedProp = response.data?.property;
        if (updatedProp) {
          setProperties((prev) =>
            prev.map((x) =>
              x.pin === updatedProp.pin
                ? { ...x, ...updatedProp, _imgBust: Date.now() }
                : x
            )
          );
        }

        setRotateState((prev) => ({ ...prev, updated: prev.updated + 1 }));
      } catch (err) {
        console.error("Rotate image error:", p.pin, err);
        setRotateState((prev) => ({ ...prev, failed: prev.failed + 1 }));
      }
    }

    setRotateState((prev) => ({
      ...prev,
      running: false,
      processed: prev.total,
      currentPin: "",
    }));
  };

  const handleFlagByAlgorithm = async (property) => {
    if (!selectedUser?.id) {
      alert("Please select a user first.");
      return;
    }

    const reason = window.prompt(
      `Enter algorithm flag reason for PIN "${property.pin}":`,
      "Flagged by algorithm based on data mismatch."
    );
    if (!reason || !reason.trim()) return;

    const scoreInput = window.prompt(
      "Enter confidence score (0 to 1, optional):",
      ""
    );

    const confidenceScore =
      scoreInput === null || String(scoreInput).trim() === ""
        ? null
        : Number(scoreInput);

    if (
      confidenceScore !== null &&
      (Number.isNaN(confidenceScore) || confidenceScore < 0 || confidenceScore > 1)
    ) {
      alert("Confidence score must be between 0 and 1.");
      return;
    }

    try {
      await axios.post("/inspector/algorithm-flagged", {
        propertyId: property.id,
        flaggedReason: reason.trim(),
        confidenceScore,
        flaggedBy: currentUser.id || null,
      });
      alert(`PIN "${property.pin}" flagged by algorithm successfully.`);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to flag property by algorithm.");
    }
  };

  const handleExport = async () => {
    if (!selectedUser) {
      alert("Please select a user first.");
      return;
    }

    try {
      const response = await axios.get(`/properties/export/comprehensive/${selectedUser.id}`);
      
      if (response.data.success && response.data.data) {
        const worksheet = XLSX.utils.json_to_sheet(response.data.data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Comprehensive Data");

        // Auto-adjust column widths
        const maxWidth = response.data.data.reduce((w, r) => {
          Object.keys(r).forEach(k => {
            w[k] = Math.max(w[k] || 10, String(r[k]).length);
          });
          return w;
        }, {});
        worksheet["!cols"] = Object.keys(maxWidth).map(k => ({ wch: Math.min(maxWidth[k] + 2, 50) }));

        const fileName = `${selectedUser.name || selectedUser.username}_comprehensive_${new Date().toISOString().split("T")[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to export data.");
    }
  };

  const handleExportClickTracking = async () => {
    try {
      const inspectorId = selectedUser?.id || null;
      console.log('🔍 handleExportClickTracking called');
      console.log('🔍 selectedUser:', selectedUser);
      console.log('🔍 inspectorId:', inspectorId);
      
      const response = await exportClickTrackingData(inspectorId);
      console.log('🔍 API Response:', response);
      
      if (response.success && response.data) {
        console.log('✅ Processing data for export');
        // Create the variable definition sheet (vertical format)
        const variableSheetData = [
          ['', '', '', '', '', ''],
          ['', '', '', '', '', ''],
          ['Variable Group', 'Variable Name', 'Description', 'Class', 'Values'],
          ['Identifiers', '', '', '', ''],
          ['', 'property_id', 'Unique property identifier (PIN)', 'String', ''],
          ['', 'inspector_id', 'Unique inspector identifier', 'String', ''],
          ['', 'inspector_name', 'Inspector name', 'String', ''],
          ['Tracking', '', '', '', ''],
          ['Clicked in the main dashboard', 'Variable Name', 'Description', 'Class', 'Values'],
          ['Clicked in the main dashboard', 'log_in_date', 'Inspector X logged in - date', 'Binary', ''],
          ['Clicked in the main dashboard', 'log_in_time', 'Inspector X logged in - time', 'Binary', ''],
          ['Clicked in the main dashboard', 'pending_review', 'Inspector X clicked in the pending review tab', 'Binary', ''],
          ['Clicked in the main dashboard', 'reviewed', 'Inspector X clicked in the reviewed tab', 'Binary', ''],
          ['Clicked in the main dashboard', 'flagged_by_AI', 'Inspector X clicked in the flagged by AI tab', 'Binary', ''],
          ['Clicked in the main dashboard', 'flagged_by_Supervisor', 'Inspector X clicked in the flagged by Supervisor tab', 'Binary', ''],
          ['Clicked in the main dashboard', 'resolved', 'Inspector X clicked in the resolved tab', 'Binary', ''],
          ['Clicked in the main dashboard', 'search_pin', 'Inspector X clicked in the search button', 'Binary', ''],
          ['Clicked in the main dashboard', 'contact_admin', 'Inspector X clicked in the contact button', 'Binary', ''],
          ['Clicked in the Pending Review Tab', '', '', '', ''],
          ['Clicked in the Pending Review Tab', 'pending_review_view_details', 'Inspector X clicked in view details', 'Binary', ''],
          ['Clicked in the Pending Review Tab', 'pending_review_all', 'Inspector X clicked in all', 'Binary', ''],
          ['Clicked in the Pending Review Tab', 'pending_review_starred', 'Inspector X clicked in starred', 'Binary', ''],
          ['Clicked in the Pending Review Tab', 'pending_review_unstarred', 'Inspector X clicked in unstarred', 'Binary', ''],
          ['Clicked in the Pending Review Tab', 'pending_review_refresh', 'Inspector X clicked in refresh', 'Binary', ''],
          ['Clicked in the Pending Review Tab', 'pending_review_page_next', 'Inspector X clicked in next', 'Binary', ''],
          ['Clicked in the Pending Review Tab', 'pending_review_page_previous', 'Inspector X clicked in previous', 'Binary', ''],
          ['Clicked in the Reviewed Page', '', '', '', ''],
          ['Clicked in the Reviewed Page', 'reviewed_reassessed_view_details', 'Inspector X clicked in view details', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_reassessed_all', 'Inspector X clicked in all', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_reassessed_starred', 'Inspector X clicked in starred', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_reassessed_unstarred', 'Inspector X clicked in unstarred', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_reassessed_refresh', 'Inspector X clicked in refresh', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_reassessed_page_next', 'Inspector X clicked in next', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_reassessed_page_previous', 'Inspector X clicked in previous', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_not_reassessed_view_details', 'Inspector X clicked in view details', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_not_reassessed_all', 'Inspector X clicked in all', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_not_reassessed_starred', 'Inspector X clicked in starred', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_not_reassessed_unstarred', 'Inspector X clicked in unstarred', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_not_reassessed_refresh', 'Inspector X clicked in refresh', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_not_reassessed_page_next', 'Inspector X clicked in next', 'Binary', ''],
          ['Clicked in the Reviewed Page', 'reviewed_not_reassessed_page_previous', 'Inspector X clicked in previous', 'Binary', ''],
          ['Clicked in Flagged by AI', '', '', '', ''],
          ['Clicked in Flagged by AI', 'flagged_by_AI_refresh', 'Inspector X clicked in refresh', 'Binary', ''],
          ['Clicked in Flagged by Supervisor', '', '', '', ''],
          ['Clicked in Flagged by Supervisor', 'flagged_by_Supervisor_view_details', 'Inspector X clicked in view details', 'Binary', ''],
          ['Clicked in Flagged by Supervisor', 'flagged_by_Supervisor_all', 'Inspector X clicked in all', 'Binary', ''],
          ['Clicked in Flagged by Supervisor', 'flagged_by_Supervisor_pending', 'Inspector X clicked in pending', 'Binary', ''],
          ['Clicked in Flagged by Supervisor', 'flagged_by_Supervisor_responded', 'Inspector X clicked in responded', 'Binary', ''],
          ['Clicked in Resolved', '', '', '', ''],
          ['Clicked in Resolved', 'resolved_all', 'Inspector X clicked in all', 'Binary', ''],
          ['Clicked in Resolved', 'resolved_reassessed', 'Inspector X clicked in reassessed', 'Binary', ''],
          ['Clicked in Resolved', 'resolved_not_reassessed', 'Inspector X clicked in not reassessed', 'Binary', ''],
          ['Clicked in the main dashboard', 'log_out_date', 'Inspector X logged out - date', 'Binary', ''],
          ['Clicked in the main dashboard', 'log_out_time', 'Inspector X logged out - time', 'Binary', ''],
          ['Clicked in the main dashboard', 'dashboard_properties_reviewed', 'Inspector X clicked in dashboard properties reviewed', 'Binary', ''],
          ['Clicked in the main dashboard', 'dashboard_pending_reviews', 'Inspector X clicked in dashboard pending reviews', 'Binary', ''],
          ['Clicked in the main dashboard', 'dashboard_flagged_by_algorithm', 'Inspector X clicked in dashboard flagged by algorithm', 'Binary', ''],
          ['Clicked in the main dashboard', 'dashboard_flagged_by_supervisor', 'Inspector X clicked in dashboard flagged by supervisor', 'Binary', ''],
          ['Clicked in the main dashboard', 'dashboard_accepted_by_supervisor', 'Inspector X clicked in dashboard accepted by supervisor', 'Binary', ''],
          ['Clicked in the main dashboard', 'dashboard_resolved_properties', 'Inspector X clicked in dashboard resolved properties', 'Binary', ''],
          ['Clicked in the main dashboard', 'flow_total_number', 'Inspector X clicked in flow total number', 'Binary', ''],
          ['Clicked in the main dashboard', 'flow_pending_review', 'Inspector X clicked in flow pending review', 'Binary', ''],
          ['Clicked in the main dashboard', 'flow_reviewed', 'Inspector X clicked in flow reviewed', 'Binary', ''],
          ['Clicked in the main dashboard', 'flow_flagged_by_ai', 'Inspector X clicked in flow flagged by ai', 'Binary', ''],
          ['Clicked in the main dashboard', 'flow_flagged_by_supervisor', 'Inspector X clicked in flow flagged by supervisor', 'Binary', ''],
          ['Clicked in the main dashboard', 'flow_resolved', 'Inspector X clicked in flow resolved', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', '', '', '', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'star', 'Inspector X clicked in star', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'do_not_reassess', 'Inspector X clicked in do not reassess', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reason_for_not_reassessing_confirm', 'Inspector X clicked in confirm', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reason_for_not_reassessing_cancel', 'Inspector X clicked in cancel', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reassess', 'Inspector X clicked in reassess', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'floor_updated', 'Inspector X clicked in floor updated', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'floor_explanation', 'Inspector X clicked in floor explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'land_use_updated_select_land_use', 'Inspector X clicked in select land use', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'land_use_updated_residential', 'Inspector X clicked in residential', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'land_use_updated_mixed', 'Inspector X clicked in mixed', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'land_use_updated_commercial', 'Inspector X clicked in commercial', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'land_use_explanation', 'Inspector X clicked in land use explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'land_area_updated', 'Inspector X clicked in land area updated', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'land_area_explanation', 'Inspector X clicked in land area explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'covered_area_updated', 'Inspector X clicked in covered area updated', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'covered_area_explanation', 'Inspector X clicked in covered area explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'overall_explanation', 'Inspector X clicked in overall explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'next', 'Inspector X clicked in next', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'cancel', 'Inspector X clicked in cancel', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'pt5_save', 'Inspector X clicked in pt5 save', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'pt5_cancel', 'Inspector X clicked in pt5 cancel', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit', 'Inspector X clicked in reviewed edit', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_floor_updated', 'Inspector X clicked in floor updated', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_floor_explanation', 'Inspector X clicked in floor explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_land_use_updated_select_land_use', 'Inspector X clicked in select land use', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_land_use_updated_residential', 'Inspector X clicked in residential', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_land_use_updated_mixed', 'Inspector X clicked in mixed', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_land_use_updated_commercial', 'Inspector X clicked in commercial', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_land_use_explanation', 'Inspector X clicked in land use explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_land_area_updated', 'Inspector X clicked in land area updated', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_land_area_explanation', 'Inspector X clicked in land area explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_covered_area_updated', 'Inspector X clicked in covered area updated', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_covered_area_explanation', 'Inspector X clicked in covered area explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_overall_explanation', 'Inspector X clicked in overall explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_save', 'Inspector X clicked in save', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_edit_cancel', 'Inspector X clicked in cancel', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_change_to_do_not_reassess', 'Inspector X clicked in change to do not reassess', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_change_to_do_not_reassess_exp', 'Inspector X clicked in explanation', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_change_to_do_not_reassess_confirm', 'Inspector X clicked in confirm', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_change_to_do_not_reassess_cancel', 'Inspector X clicked in cancel', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'reviewed_close_details', 'Inspector X clicked in close details', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_Supervisor_inspection_comments', 'Inspector X clicked in inspection comments', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_Supervisor_comments_save', 'Inspector X clicked in comments save', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_Supervisor_edit_do_not_reassess', 'Inspector X clicked in edit do not reassess', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_Supervisor_edit_reassess', 'Inspector X clicked in edit reassess', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_S_edit_reassess_inspector_edit_floor', 'Inspector X clicked in edit floor', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_S_edit_reassess_inspector_edit_select_land_use', 'Inspector X clicked in select land use', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_S_edit_reassess_inspector_edit_land_area', 'Inspector X clicked in edit land area', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_S_edit_reassess_inspector_edit_covered_area', 'Inspector X clicked in edit covered area', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_S_edit_reassess_add_comments', 'Inspector X clicked in add comments', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_S_edit_reassess_submit_reassessment', 'Inspector X clicked in submit reassessment', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_S_edit_reassess_cancel', 'Inspector X clicked in cancel', 'Binary', ''],
          ['All of these will have a property ID with them because these actions will be performed for one single property at a time', 'flagged_by_Supervisor_close', 'Inspector X clicked in close', 'Binary', ''],
        ];

        console.log('📋 variableSheetData rows:', variableSheetData.length);

        // Create data sheet (horizontal format with all variables as columns)
        const variableEntries = variableSheetData
          .filter(row => row[1] && row[1] !== 'Variable Name')
          .map(row => ({
            variableName: row[1],
            description: row[2],
            class: row[3]
          }));
        console.log('📋 variableEntries:', variableEntries.length, 'entries');
        
        // EXACT ORDER from screenshots! (with section info)
        const sections = [
          {
            name: 'Clicked in the main dashboard',
            color: 'C6E0B4', // light green
            variables: ['log_in_date', 'log_in_time', 'pending_review', 'reviewed', 'flagged_by_AI', 'flagged_by_Supervisor', 'resolved', 'search_pin', 'contact_admin', 'dashboard_properties_reviewed', 'dashboard_pending_reviews', 'dashboard_flagged_by_algorithm', 'dashboard_flagged_by_supervisor', 'dashboard_accepted_by_supervisor', 'dashboard_resolved_properties', 'flow_total_number', 'flow_pending_review', 'flow_reviewed', 'flow_flagged_by_ai', 'flow_flagged_by_supervisor', 'flow_resolved']
          },
          {
            name: 'Clicked in the Pending Review Tab',
            color: '92C5DE', // light blue
            variables: ['pending_review_view_details', 'pending_review_all', 'pending_review_starred', 'pending_review_unstarred', 'pending_review_refresh', 'pending_review_page_next', 'pending_review_page_previous']
          },
          {
            name: 'Clicked in the Reviewed Page',
            color: 'F4CAE4', // light pink
            variables: ['reviewed_reassessed_view_details', 'reviewed_reassessed_all', 'reviewed_reassessed_starred', 'reviewed_reassessed_unstarred', 'reviewed_reassessed_refresh', 'reviewed_reassessed_page_next', 'reviewed_reassessed_page_previous', 'reviewed_not_reassessed_view_details', 'reviewed_not_reassessed_all', 'reviewed_not_reassessed_starred', 'reviewed_not_reassessed_unstarred', 'reviewed_not_reassessed_refresh', 'reviewed_not_reassessed_page_next', 'reviewed_not_reassessed_page_previous']
          },
          {
            name: 'Clicked in Flagged by AI',
            color: 'FEE090', // light orange
            variables: ['flagged_by_AI_refresh']
          },
          {
            name: 'Clicked in Flagged by Supervisor',
            color: 'D5E8D4', // light green 2
            variables: ['flagged_by_Supervisor_view_details', 'flagged_by_Supervisor_all', 'flagged_by_Supervisor_pending', 'flagged_by_Supervisor_responded']
          },
          {
            name: 'Clicked in Resolved',
            color: 'E1D5E7', // light purple
            variables: ['resolved_all', 'resolved_reassessed', 'resolved_not_reassessed']
          },
          {
            name: 'All of these will have a property ID with them because these actions will be performed for one single property at a time',
            color: 'FFD966', // yellow
            variables: ['star', 'do_not_reassess', 'reason_for_not_reassessing_confirm', 'reason_for_not_reassessing_cancel', 'reassess', 'floor_updated', 'floor_explanation', 'land_use_updated_select_land_use', 'land_use_updated_residential', 'land_use_updated_mixed', 'land_use_updated_commercial', 'land_use_explanation', 'land_area_updated', 'land_area_explanation', 'covered_area_updated', 'covered_area_explanation', 'overall_explanation', 'next', 'cancel', 'pt5_save', 'pt5_cancel', 'reviewed_edit', 'reviewed_edit_floor_updated', 'reviewed_edit_floor_explanation', 'reviewed_edit_land_use_updated_select_land_use', 'reviewed_edit_land_use_updated_residential', 'reviewed_edit_land_use_updated_mixed', 'reviewed_edit_land_use_updated_commercial', 'reviewed_edit_land_use_explanation', 'reviewed_edit_land_area_updated', 'reviewed_edit_land_area_explanation', 'reviewed_edit_covered_area_updated', 'reviewed_edit_covered_area_explanation', 'reviewed_edit_overall_explanation', 'reviewed_edit_save', 'reviewed_edit_cancel', 'reviewed_change_to_do_not_reassess', 'reviewed_change_to_do_not_reassess_exp', 'reviewed_change_to_do_not_reassess_confirm', 'reviewed_change_to_do_not_reassess_cancel', 'reviewed_close_details', 'flagged_by_Supervisor_inspection_comments', 'flagged_by_Supervisor_comments_save', 'flagged_by_Supervisor_edit_do_not_reassess', 'flagged_by_Supervisor_edit_reassess', 'flagged_by_S_edit_reassess_inspector_edit_floor', 'flagged_by_S_edit_reassess_inspector_edit_select_land_use', 'flagged_by_S_edit_reassess_inspector_edit_land_area', 'flagged_by_S_edit_reassess_inspector_edit_covered_area', 'flagged_by_S_edit_reassess_add_comments', 'flagged_by_S_edit_reassess_submit_reassessment', 'flagged_by_S_edit_reassess_cancel', 'flagged_by_Supervisor_close']
          }
        ];

        // Split sections into non-yellow and yellow
        const nonYellowSections = sections.slice(0, 6);
        const yellowSection = sections[6];
        
        // Extract variable orders
        const nonYellowVariables = nonYellowSections.flatMap(section => section.variables);
        const yellowVariables = yellowSection.variables;
        const exactVariableOrder = [...nonYellowVariables, ...yellowVariables];

        // Build dataSheetData with section headers!
        const dataSheetData = [];
        
        // First row: section headers (merged)
        const sectionHeaderRow = [];
        // Empty first 3 cells (for property_id, inspector_id, inspector_name)
        sectionHeaderRow.push('', '', '');
        let currentCol = 3;
        
        nonYellowSections.forEach(section => {
          sectionHeaderRow.push(section.name);
          for (let i = 1; i < section.variables.length; i++) {
            sectionHeaderRow.push('');
          }
          currentCol += section.variables.length;
        });
        
        // Add a section header for "Property PIN" before yellow section
        sectionHeaderRow.push('Property PIN');
        
        // Add yellow section header
        sectionHeaderRow.push(yellowSection.name);
        for (let i = 1; i < yellowVariables.length; i++) {
          sectionHeaderRow.push('');
        }
        
        dataSheetData.push(sectionHeaderRow);

        // Second row: variable names
        const variableNameRow = [
          'property_id',
          'inspector_id',
          'inspector_name',
          ...nonYellowVariables,
          '', // Empty cell for Property PIN label column
          ...yellowVariables
        ];
        dataSheetData.push(variableNameRow);

        // Then add the data rows
        (response.data || []).forEach(item => {
          const row = [];
          row.push(item.property_id);
          row.push(item.inspector_id);
          row.push(item.inspector_name);
          
          nonYellowVariables.forEach(variable => {
            row.push(item[variable] ?? 0);
          });
          
          // Add property PIN again in the new column
          row.push(item.property_id);
          
          yellowVariables.forEach(variable => {
            row.push(item[variable] ?? 0);
          });
          
          dataSheetData.push(row);
        });
        console.log('📋 dataSheetData rows:', dataSheetData.length);

        // Deduplicate raw clicks in frontend
        const rawClicks = response.data.rawClicks || [];
        const seenIds = new Set();
        const uniqueRawClicks = [];
        rawClicks.forEach(click => {
          if (!seenIds.has(click.id)) {
            seenIds.add(click.id);
            uniqueRawClicks.push(click);
          }
        });

        // Create raw click data sheet
        const rawClickData = [
          RAW_CLICK_HEADERS,
          ...uniqueRawClicks.map(mapRawClickRow),
        ];
        console.log('📋 rawClickData rows:', rawClickData.length);

        const workbook = XLSX.utils.book_new();
        const sampleSheet = XLSX.utils.aoa_to_sheet(dataSheetData);
        const variableSheet = XLSX.utils.aoa_to_sheet(variableSheetData);
        const rawClickSheet = XLSX.utils.aoa_to_sheet(rawClickData);

        // Set column widths for Raw Click Data sheet
        rawClickSheet['!cols'] = RAW_CLICK_COL_WIDTHS;

        XLSX.utils.book_append_sheet(workbook, sampleSheet, 'Sample Data');
        XLSX.utils.book_append_sheet(workbook, variableSheet, 'Tracking Variables');
        XLSX.utils.book_append_sheet(workbook, rawClickSheet, 'Raw Click Data');

        const fileName = `click_tracking_${new Date().toISOString().split("T")[0]}_${Date.now()}.xlsx`;
        console.log('💾 Saving file:', fileName);
        
        // Create a proper download link for the browser
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('✅ File saved successfully!');
        alert(`Successfully downloaded ${fileName} with:\n- ${(response.data || []).length} summary rows\n- ${(response.rawClicks || []).length} raw click events`);
      } else {
        console.warn('⚠️ Response did not have success/data:', response);
      }
    } catch (err) {
      console.error('❌ Error exporting:', err);
      alert(err.response?.data?.error || "Failed to export click tracking data.");
    }
  };

  const handleExportRawClickTracking = async () => {
    if (rawExportRunning.current) return;
    rawExportRunning.current = true;
    setRawExportStage('Downloading click events…');
    try {
      const inspectorId = selectedUser?.id || null;
      console.log('🔍 handleExportRawClickTracking called');
      
      const response = await exportClickTrackingData(inspectorId, true);
      console.log('🔍 API Response:', response);
      
      if (response.success && response.rawClicks) {
        setRawExportStage('Preparing Excel file…');
        // Paint the loading message before generating the workbook.
        await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
        // Deduplicate raw clicks in frontend
        const rawClicks = response.rawClicks || [];
        const seenIds = new Set();
        const uniqueRawClicks = [];
        rawClicks.forEach(click => {
          if (!seenIds.has(click.id)) {
            seenIds.add(click.id);
            uniqueRawClicks.push(click);
          }
        });

        // Create raw click data sheet
        const rawClickData = [
          RAW_CLICK_HEADERS,
          ...uniqueRawClicks.map(mapRawClickRow),
        ];
        console.log('📋 rawClickData rows:', rawClickData.length);

        const workbook = XLSX.utils.book_new();
        const rawClickSheet = XLSX.utils.aoa_to_sheet(rawClickData);
        // Set column widths for Raw Click Events sheet
        rawClickSheet['!cols'] = RAW_CLICK_COL_WIDTHS;
        XLSX.utils.book_append_sheet(workbook, rawClickSheet, 'Raw Click Events');

        const fileName = `raw_click_events_${new Date().toISOString().split("T")[0]}_${Date.now()}.xlsx`;
        console.log('💾 Saving file:', fileName);
        
        // Create a proper download link for the browser
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        
        console.log('✅ File saved successfully!');

      } else {
        console.warn('⚠️ Response did not have success/rawClicks:', response);
      }
    } catch (err) {
      console.error('❌ Error exporting raw click data:', err);
      alert(err.response?.data?.error || (err.code === 'ECONNABORTED'
        ? 'Raw click export timed out. Please try again or check the server export logs.'
        : err.message || 'Failed to export raw click data.'));
    } finally {
      rawExportRunning.current = false;
      setRawExportStage('');
    }
  };

  const handleExportSupervisorClickTracking = async () => {
    try {
      const supervisorId = selectedUser?.id || null;
      console.log('🔍 handleExportSupervisorClickTracking called');
      
      const response = await exportSupervisorClickTrackingData(supervisorId);
      console.log('🔍 API Response:', response);
      
      if (response.success && response.data && response.actionColumns) {
        // Create supervisor-specific variable sheet
        const supervisorVariableSheetData = [
          ['', '', '', '', '', ''],
          ['', '', '', '', '', ''],
          ['Variable Group', 'Variable Name', 'Description', 'Class', 'Values'],
          ['Identifiers', '', '', '', ''],
          ['', 'property_id', 'Unique property identifier (PIN)', 'String', ''],
          ['', 'supervisor_id', 'Unique supervisor identifier', 'String', ''],
          ['', 'supervisor_name', 'Supervisor name', 'String', ''],
          ['Tracking', '', '', '', ''],
          ['Clicked in the main dashboard', 'log_in_date', 'Supervisor X logged in - date', 'Binary', ''],
          ['Clicked in the main dashboard', 'log_in_time', 'Supervisor X logged in - time', 'Binary', ''],
          ['Clicked in the main dashboard', 'dashboard', 'Supervisor X opened dashboard', 'Binary', ''],
          ['Clicked in the main dashboard', 'property_view', 'Supervisor X opened property view', 'Binary', ''],
          ['Clicked in the main dashboard', 'resolved_view', 'Supervisor X opened resolved view', 'Binary', ''],
          ['Clicked in the main dashboard', 'inspectors_view', 'Supervisor X opened inspectors view', 'Binary', ''],
          ['Clicked in the main dashboard', 'flagged_view', 'Supervisor X opened flagged view', 'Binary', ''],
          ['Clicked in the main dashboard', 'assigned_view', 'Supervisor X opened assigned view', 'Binary', ''],
          ['Clicked in the property page', '', '', '', ''],
          ['Clicked in the property page', 'property_view_details', 'Supervisor X viewed property details', 'Binary', ''],
          ['Clicked in the property page', 'property_circle_filter', 'Supervisor X used circle filter', 'Binary', ''],
          ['Clicked in the property page', 'property_search', 'Supervisor X searched property', 'Binary', ''],
          ['Clicked in the property page', 'property_refresh', 'Supervisor X refreshed properties', 'Binary', ''],
          ['Clicked in the property page', 'property_page_prev', 'Supervisor X clicked previous page', 'Binary', ''],
          ['Clicked in the property page', 'property_page_next', 'Supervisor X clicked next page', 'Binary', ''],
          ['Clicked in the property page', 'property_close_details', 'Supervisor X closed property details', 'Binary', ''],
          ['Clicked in the property page', 'property_image_rotate', 'Supervisor X rotated property image', 'Binary', ''],
          ['Clicked in the property page', 'property_image_rotate_save', 'Supervisor X saved rotated property image', 'Binary', ''],
          ['Clicked in the property page', 'property_accept', 'Supervisor X accepted property', 'Binary', ''],
          ['Clicked in the property page', 'property_flag', 'Supervisor X flagged property with comment', 'Binary', ''],
          ['Clicked in the main dashboard', 'log_out_date', 'Supervisor X logged out - date', 'Binary', ''],
          ['Clicked in the main dashboard', 'log_out_time', 'Supervisor X logged out - time', 'Binary', ''],
        ];

        console.log('📋 supervisorVariableSheetData rows:', supervisorVariableSheetData.length);

        // Create supervisor data sheet (horizontal format)
        const supervisorSections = [
          {
            name: 'Clicked in the main dashboard',
            color: 'C6E0B4',
            variables: ['log_in_date', 'log_in_time', 'dashboard', 'property_view', 'resolved_view', 'inspectors_view', 'flagged_view', 'assigned_view', 'log_out_date', 'log_out_time']
          },
          {
            name: 'Clicked in the property page',
            color: '92C5DE',
            variables: ['property_view_details', 'property_circle_filter', 'property_search', 'property_refresh', 'property_page_prev', 'property_page_next', 'property_close_details', 'property_image_rotate', 'property_image_rotate_save', 'property_accept', 'property_flag']
          }
        ];

        const supervisorNonYellowVariables = supervisorSections[0].variables;
        const supervisorYellowVariables = supervisorSections[1].variables;
        const supervisorExactVariableOrder = [...supervisorNonYellowVariables, ...supervisorYellowVariables];

        // Build supervisor data sheet
        const supervisorDataSheetData = [];
        
        // First row: section headers
        const supervisorSectionHeaderRow = ['', '', ''];
        supervisorSections.forEach(section => {
          supervisorSectionHeaderRow.push(section.name);
          for (let i = 1; i < section.variables.length; i++) {
            supervisorSectionHeaderRow.push('');
          }
        });
        supervisorDataSheetData.push(supervisorSectionHeaderRow);

        // Second row: variable names
        const supervisorVariableNameRow = [
          'property_id',
          'supervisor_id',
          'supervisor_name'
        ];
        supervisorExactVariableOrder.forEach(variable => {
          supervisorVariableNameRow.push(variable);
        });
        supervisorDataSheetData.push(supervisorVariableNameRow);

        // Now add the data rows!
        response.data.forEach(item => {
          const row = supervisorExactVariableOrder.reduce((arr, key) => {
            arr.push(item[key]);
            return arr;
          }, [item.property_id, item.supervisor_id, item.supervisor_name]);
          supervisorDataSheetData.push(row);
        });

        console.log('📋 supervisorDataSheetData rows:', supervisorDataSheetData.length);

        // Deduplicate raw clicks
        const rawClicks = response.rawClicks || [];
        const seenIds = new Set();
        const uniqueRawClicks = [];
        rawClicks.forEach(click => {
          if (!seenIds.has(click.id)) {
            seenIds.add(click.id);
            uniqueRawClicks.push(click);
          }
        });

        // Create raw click data sheet
        const rawClickData = [
          SUPERVISOR_RAW_CLICK_HEADERS,
          ...uniqueRawClicks.map(mapSupervisorRawClickRow),
        ];
        console.log('📋 supervisorRawClickData rows:', rawClickData.length);

        const workbook = XLSX.utils.book_new();
        const sampleSheet = XLSX.utils.aoa_to_sheet(supervisorDataSheetData);
        const variableSheet = XLSX.utils.aoa_to_sheet(supervisorVariableSheetData);
        const rawClickSheet = XLSX.utils.aoa_to_sheet(rawClickData);

        // Set column widths for Raw Click Data sheet
        rawClickSheet['!cols'] = SUPERVISOR_RAW_CLICK_COL_WIDTHS;

        XLSX.utils.book_append_sheet(workbook, sampleSheet, 'Sample Data');
        XLSX.utils.book_append_sheet(workbook, variableSheet, 'Tracking Variables');
        XLSX.utils.book_append_sheet(workbook, rawClickSheet, 'Raw Click Data');

        const fileName = `supervisor_click_tracking_${new Date().toISOString().split("T")[0]}_${Date.now()}.xlsx`;
        console.log('💾 Saving file:', fileName);
        
        // Create a proper download link for the browser
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('✅ File saved successfully!');
        alert(`Successfully downloaded ${fileName} with:\n- ${(response.data || []).length} summary rows\n- ${(response.rawClicks || []).length} raw click events`);
      } else {
        console.warn('⚠️ Response did not have success/data:', response);
      }
    } catch (err) {
      console.error('❌ Error exporting supervisor click tracking:', err);
      alert(err.response?.data?.error || "Failed to export supervisor click tracking data.");
    }
  };

  const handleExportRawSupervisorClickTracking = async () => {
    if (rawExportRunning.current) return;
    rawExportRunning.current = true;
    setRawExportStage('Downloading click events…');
    try {
      const supervisorId = selectedUser?.id || null;
      console.log('🔍 handleExportRawSupervisorClickTracking called');
      
      const response = await exportSupervisorClickTrackingData(supervisorId, true);
      console.log('🔍 API Response:', response);
      
      if (response.success && response.rawClicks) {
        setRawExportStage('Preparing Excel file…');
        // Paint the loading message before generating the workbook.
        await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
        // Deduplicate raw clicks in frontend
        const rawClicks = response.rawClicks || [];
        const seenIds = new Set();
        const uniqueRawClicks = [];
        rawClicks.forEach(click => {
          if (!seenIds.has(click.id)) {
            seenIds.add(click.id);
            uniqueRawClicks.push(click);
          }
        });

        // Create raw click data sheet
        const rawClickData = [
          SUPERVISOR_RAW_CLICK_HEADERS,
          ...uniqueRawClicks.map(mapSupervisorRawClickRow),
        ];
        console.log('📋 rawClickData rows:', rawClickData.length);

        const workbook = XLSX.utils.book_new();
        const rawClickSheet = XLSX.utils.aoa_to_sheet(rawClickData);
        // Set column widths for Raw Click Events sheet
        rawClickSheet['!cols'] = SUPERVISOR_RAW_CLICK_COL_WIDTHS;
        XLSX.utils.book_append_sheet(workbook, rawClickSheet, 'Raw Click Events');

        const fileName = `supervisor_raw_click_events_${new Date().toISOString().split("T")[0]}_${Date.now()}.xlsx`;
        console.log('💾 Saving file:', fileName);
        
        // Create a proper download link for the browser
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        
        console.log('✅ File saved successfully!');

      } else {
        console.warn('⚠️ Response did not have success/rawClicks:', response);
      }
    } catch (err) {
      console.error('❌ Error exporting raw supervisor click data:', err);
      alert(err.response?.data?.error || "Failed to export raw supervisor click data.");
    } finally {
      rawExportRunning.current = false;
      setRawExportStage('');
    }
  };

  const handleDeleteSupervisorClickTracking = async () => {
    if (!selectedUser) {
      alert("Please select a user first.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ALL click tracking data for supervisor ${selectedUser.name}? This cannot be undone!`)) {
      return;
    }
    try {
      const response = await deleteSupervisorClickTrackingData(selectedUser.id);
      if (response.success) {
        alert(response.message);
      }
    } catch (err) {
      console.error('❌ Error deleting supervisor click tracking data:', err);
      alert(err.response?.data?.error || "Failed to delete supervisor click tracking data.");
    }
  };

  const handleDeleteClickTracking = async () => {
    if (!selectedUser) {
      alert("Please select a user first.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ALL click tracking data for ${selectedUser.name}? This cannot be undone!`)) {
      return;
    }
    try {
      const response = await deleteClickTrackingData(selectedUser.id);
      if (response.success) {
        alert(response.message);
      }
    } catch (err) {
      console.error('❌ Error deleting click tracking data:', err);
      alert(err.response?.data?.error || "Failed to delete click tracking data.");
    }
  };

  const handleDeleteAllClickTracking = async () => {
    if (!window.confirm("Are you sure you want to delete ALL click tracking data for ALL users? This cannot be undone!")) {
      return;
    }
    try {
      const response = await deleteClickTrackingData();
      if (response.success) {
        alert(response.message);
      }
    } catch (err) {
      console.error('❌ Error deleting all click tracking data:', err);
      alert(err.response?.data?.error || "Failed to delete click tracking data.");
    }
  };

  // ── Role badge ─────────────────────────────────────────────────────────────
  const roleBadge = (role) => {
    if (role === "supervisor")
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
          Supervisor
        </span>
      );
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        Inspector
      </span>
    );
  };

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Sidebar />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto">

          {/* Page header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-1">
                Manage Assignments
              </h1>
              <p className="text-gray-500 text-lg">
                View, edit, and delete properties assigned to any inspector or supervisor.
              </p>
            </div>
            <button
              onClick={handleDeleteAllProperties}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition shadow"
            >
              🗑️ Delete All Properties
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">

            {/* ── Left panel: User picker ───────────────────────────────── */}
            <div className="xl:col-span-1">
              <div className="bg-white rounded-2xl shadow-md border border-gray-100 sticky top-6 overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-slate-700 to-slate-800">
                  <h2 className="text-white font-bold text-lg">Select User</h2>
                  <p className="text-slate-400 text-xs mt-0.5">
                    {filteredUsers.length} user(s)
                  </p>
                </div>

                {/* Role filter tabs */}
                <div className="flex border-b border-gray-100">
                  {["inspector", "supervisor"].map((r) => (
                    <button
                      key={r}
                      onClick={() => setRoleFilter(r)}
                      className={`flex-1 py-2 text-xs font-semibold capitalize transition ${roleFilter === r
                          ? "bg-blue-600 text-white"
                          : "text-gray-500 hover:bg-gray-50"
                        }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>

                {/* Search */}
                <div className="p-3 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search name, username, circle…"
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                  />
                </div>

                {/* User list */}
                <div className="overflow-y-auto max-h-[calc(100vh-320px)]">
                  {usersError && (
                    <div className="p-4 text-center text-red-500 text-sm">
                      {usersError}
                    </div>
                  )}
                  {filteredUsers.length === 0 && !usersError ? (
                    <div className="p-6 text-center text-gray-400 text-sm">
                      No users found
                    </div>
                  ) : (
                    filteredUsers.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => setSelectedUser(u)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-50 transition hover:bg-blue-50 ${selectedUser?.id === u.id
                            ? "bg-blue-50 border-l-4 border-l-blue-600"
                            : ""
                          }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-gray-800 text-sm truncate">
                            {u.name}
                          </span>
                          {roleBadge(u.role)}
                        </div>
                        <div className="text-xs text-gray-400">
                          @{u.username}
                          {u.circle ? ` · ${u.circle}` : ""}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* ── Right panel: Properties table ─────────────────────────── */}
            <div className="xl:col-span-3">
              {!selectedUser ? (
                <div className="bg-white rounded-2xl shadow-md border border-gray-100 flex flex-col items-center justify-center py-24 text-center">
                  <div className="text-6xl mb-4">👈</div>
                  <h3 className="text-xl font-bold text-gray-700 mb-2">
                    Select a user to get started
                  </h3>
                  <p className="text-gray-400 text-sm max-w-xs">
                    Choose an inspector or supervisor from the left panel to view
                    and manage their assigned properties.
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">

                  {/* Panel header */}
                  <div className="p-5 border-b border-gray-100">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h2 className="text-xl font-bold text-gray-900">
                            {selectedUser.name}
                          </h2>
                          {roleBadge(selectedUser.role)}
                        </div>
                        <p className="text-sm text-gray-500">
                          @{selectedUser.username}
                          {selectedUser.circle ? ` · Circle: ${selectedUser.circle}` : ""}
                          {selectedUser.rank ? ` · Rank: ${selectedUser.rank}` : ""}
                        </p>
                      </div>

                      {/* Tabs for supervisors */}
                      {selectedUser.role === "supervisor" && (
                        <div className="flex bg-gray-100 p-1 rounded-xl">
                          <button
                            onClick={() => setSupervisorPropertyTab("assigned")}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${supervisorPropertyTab === "assigned" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                          >
                            Assigned Properties ({assignedProperties.length})
                          </button>
                          <button
                            onClick={() => setSupervisorPropertyTab("uploaded")}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${supervisorPropertyTab === "uploaded" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                          >
                            Uploaded Properties ({uploadedProperties.length})
                          </button>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2">
                        {selectedUser.role !== "supervisor" && filteredProperties.some((p) => !!p.image) && (
                          <button
                            onClick={handleRotateImages}
                            disabled={rotateState.running}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition shadow"
                          >
                            🔄 Rotate Images
                          </button>
                        )}
                        {properties.length > 0 && selectedUser.role !== "supervisor" && (
                          <button
                            onClick={handleExport}
                            disabled={rotateState.running}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition shadow"
                          >
                            📥 Download Data
                          </button>
                        )}

                        {selectedUser && roleFilter !== 'supervisor' && (
                          <>
                           {/*  <button
                              onClick={handleExportClickTracking}
                              disabled={rotateState.running}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition shadow"
                            >
                              📊 Export Click Tracking
                            </button> */}
                            <button
                              onClick={handleExportRawClickTracking}
                              disabled={rotateState.running || !!rawExportStage}
                              aria-busy={!!rawExportStage}
                              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-xl transition shadow disabled:opacity-60 disabled:cursor-wait"
                            >
                              {rawExportStage ? (
                                <>
                                  <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                  <span role="status">{rawExportStage}</span>
                                </>
                              ) : '📥 Download Raw Click Events'}
                            </button>
                            {false && (
                            <button
                              onClick={handleDeleteClickTracking}
                              disabled={rotateState.running}
                              className="flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-semibold rounded-xl transition shadow"
                            >
                              🗑 Delete Click Data
                            </button>
                            )}
                          </>
                        )}
                        
                        {selectedUser && roleFilter === 'supervisor' && (      
                           <>
                          {/*   <button
                              onClick={handleExportSupervisorClickTracking}    
                              disabled={rotateState.running}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition shadow"
                            >
                              📊 Export Supervisor Click Tracking
                            </button> */}
                             <button
                              onClick={handleExportRawSupervisorClickTracking}
                              disabled={rotateState.running || !!rawExportStage}
                              aria-busy={!!rawExportStage}
                              className="flex items-center gap-2 px-4 py-2 bg-purple-700 hover:bg-purple-800 text-white text-sm font-semibold rounded-xl transition shadow disabled:opacity-60 disabled:cursor-wait"
                            >
                              {rawExportStage ? (
                                <>
                                  <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                  <span role="status">{rawExportStage}</span>
                                </>
                              ) : '📥 Download Supervisor Raw Click Events'}
                            </button>
                            <button
                              onClick={handleDeleteSupervisorClickTracking}
                              disabled={rotateState.running}
                              className="flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-semibold rounded-xl transition shadow"
                            >
                              🗑 Delete Supervisor Click Data
                            </button>
                          </>
                        )}
                        
                       {/*  <button
                          onClick={handleDeleteAllClickTracking}
                          disabled={rotateState.running}
                          className="flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-semibold rounded-xl transition shadow"
                        >
                          🗑 Delete ALL Click Data
                        </button> */}
                        {selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded" ? (
                          <>
                            {selectedUploadedIds.length > 0 && (
                              <button
                                onClick={handleDeleteSelectedUploaded}
                                disabled={rotateState.running}
                                className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition shadow"
                              >
                                🗑 Delete Selected ({selectedUploadedIds.length})
                              </button>
                            )}
                            {uploadedProperties.length > 0 && (
                              <button
                                onClick={handleDeleteAllUploaded}
                                disabled={rotateState.running}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition shadow"
                              >
                                ✕ Delete All ({uploadedProperties.length})
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            {selectedPins.length > 0 && (
                              <button
                                onClick={handleDeleteSelected}
                                disabled={rotateState.running}
                                className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition shadow"
                              >
                                🗑 Delete Selected ({selectedPins.length})
                              </button>
                            )}
                            {properties.length > 0 && (
                              <button
                                onClick={handleDeleteAll}
                                disabled={rotateState.running}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition shadow"
                              >
                                ✕ Remove All ({properties.length})
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {(rotateState.running ||
                      rotateState.updated ||
                      rotateState.skipped ||
                      rotateState.failed) && (
                        <div className="mt-3 text-xs text-gray-500 flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span>
                            {rotateState.running
                              ? `Rotating… ${rotateState.processed + 1}/${rotateState.total}${rotateState.currentPin ? ` (PIN: ${rotateState.currentPin})` : ""
                              }`
                              : `Rotate complete (${rotateState.total} checked)`}
                          </span>
                          <span>Updated: {rotateState.updated}</span>
                          <span>Skipped: {rotateState.skipped}</span>
                          <span>Failed: {rotateState.failed}</span>
                        </div>
                      )}

                    {/* Stats row */}
                    <div className="flex flex-wrap gap-4 mt-4">
                      <div className="bg-blue-50 rounded-xl px-4 py-2 text-center">
                        <div className="text-xl font-bold text-blue-700">
                          {properties.length}
                        </div>
                        <div className="text-xs text-blue-500">Total</div>
                      </div>
                      <div className="bg-orange-50 rounded-xl px-4 py-2 text-center">
                        <div className="text-xl font-bold text-orange-600">
                          {selectedPins.length}
                        </div>
                        <div className="text-xs text-orange-500">Selected</div>
                      </div>
                      <div className="bg-green-50 rounded-xl px-4 py-2 text-center">
                        <div className="text-xl font-bold text-green-700">
                          {properties.filter((p) => p.status === "resolved").length}
                        </div>
                        <div className="text-xs text-green-500">Resolved</div>
                      </div>
                      <div className="bg-purple-50 rounded-xl px-4 py-2 text-center">
                        <div className="text-xl font-bold text-purple-700">
                          {properties.filter((p) => !!p.image).length}
                        </div>
                        <div className="text-xs text-purple-500">With Image</div>
                      </div>
                    </div>
                  </div>

                  {/* Search bar and filters */}
                  <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-3">
                    <input
                      type="text"
                      placeholder="Search PIN, circle, location, status…"
                      value={propSearch}
                      onChange={(e) => setPropSearch(e.target.value)}
                      className="flex-1 min-w-[200px] border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                    <select
                      value={imageFilter}
                      onChange={(e) => setImageFilter(e.target.value)}
                      className="border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="all">All Properties</option>
                      <option value="has-image">With Images</option>
                      <option value="no-image">Without Images</option>
                    </select>
                  </div>

                  {/* Table */}
                  {loadingProps ? (
                    <div className="py-20 text-center text-gray-400">
                      <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
                      <p className="text-sm">Loading properties…</p>
                    </div>
                  ) : propsError ? (
                    <div className="py-20 text-center text-red-500 text-sm">
                      {propsError}
                    </div>
                  ) : filteredProperties.length === 0 ? (
                    <div className="py-20 text-center text-gray-400 text-sm">
                      {propSearch
                        ? "No properties match your search."
                        : selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded"
                        ? "No uploaded properties found for this user."
                        : "No assigned properties found for this user."}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="py-3 px-4 text-left">
                              <input
                                type="checkbox"
                                checked={
                                  (selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded"
                                    ? selectedUploadedIds.length === filteredProperties.length
                                    : selectedPins.length === filteredProperties.length) &&
                                  filteredProperties.length > 0
                                }
                                onChange={() => {
                                  if (selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded") {
                                    toggleAllUploaded();
                                  } else {
                                    toggleAll();
                                  }
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600"
                              />
                            </th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600">
                              PIN
                            </th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600">
                              Circle
                            </th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600">
                              Location
                            </th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600">
                              Status
                            </th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600">
                              Occupation
                            </th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600">
                              Admin GARV
                            </th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600">
                              Image
                            </th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filteredProperties.map((p, idx) => (
                            <tr
                              key={selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded" ? p.id : p.pin}
                              className={`transition hover:bg-blue-50 ${
                                  selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded"
                                    ? (selectedUploadedIds.includes(p.id) ? "bg-orange-50" : idx % 2 === 0 ? "bg-white" : "bg-gray-50/50")
                                    : (selectedPins.includes(p.pin) ? "bg-orange-50" : idx % 2 === 0 ? "bg-white" : "bg-gray-50/50")
                                }`}
                            >
                              <td className="py-3 px-4">
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded"
                                      ? selectedUploadedIds.includes(p.id)
                                      : selectedPins.includes(p.pin)
                                  }
                                  onChange={() => {
                                    if (selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded") {
                                      toggleUploadedId(p.id);
                                    } else {
                                      togglePin(p.pin);
                                    }
                                  }}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                                />
                              </td>
                              <td className="py-3 px-4 font-mono font-bold text-gray-800">
                                {p.pin}
                              </td>
                              <td className="py-3 px-4">
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  {p.circle || "—"}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-gray-600 max-w-[140px] truncate">
                                {p.location || "—"}
                              </td>
                              <td className="py-3 px-4">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.status === "resolved"
                                      ? "bg-green-100 text-green-700"
                                      : p.status === "flagged"
                                        ? "bg-red-100 text-red-700"
                                        : p.status === "new"
                                          ? "bg-blue-100 text-blue-700"
                                          : "bg-gray-100 text-gray-600"
                                    }`}
                                >
                                  {p.status || "—"}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-gray-600">
                                {p.occupationstatus || "—"}
                              </td>
                              <td className="py-3 px-4 text-gray-700 font-medium">
                                {p.admin_garv ?? "—"}
                              </td>
                              <td className="py-3 px-4">
                                {p.image ? (
                                  <img
                                    src={appendCacheBuster(
                                      getImageUrl(p.image),
                                      p._imgBust
                                    )}
                                    alt="prop"
                                    style={{ imageOrientation: "from-image" }}
                                    className="w-10 h-10 object-cover rounded-lg border border-gray-200 shadow-sm"
                                  />
                                ) : (
                                  <span className="text-gray-300 text-xs">No img</span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  {/* Only show Edit if not in uploaded tab */}
                                  {!(selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded") && (
                                    <button
                                      onClick={() => setEditingProperty(p)}
                                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition"
                                    >
                                      ✏️ Edit
                                    </button>
                                  )}
                                  {selectedUser.role !== "supervisor" && (
                                    <button
                                      onClick={() => handleFlagByAlgorithm(p)}
                                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg transition"
                                    >
                                      🤖 Flag
                                    </button>
                                  )}
                                  {/* Delete single */}
                                  <button
                                    onClick={() => {
                                      if (selectedUser.role === "supervisor" && supervisorPropertyTab === "uploaded") {
                                        handleDeleteSingleUploaded(p);
                                      } else {
                                        setConfirm({
                                          message: `Delete assignment for PIN "${p.pin}" from "${selectedUser?.name}"?`,
                                          onConfirm: async () => {
                                            setConfirm(null);
                                            try {
                                              if (selectedUser.role === "supervisor") {
                                                await axios.delete(
                                                  "/properties/assignments/bulk-delete-supervisor",
                                                  {
                                                    data: {
                                                      supervisorId: selectedUser.id,
                                                      pins: [p.pin],
                                                    },
                                                  }
                                                );
                                              } else {
                                                await axios.delete(
                                                  "/properties/assignments/bulk-delete",
                                                  {
                                                    data: {
                                                      inspectorId: selectedUser.id,
                                                      pins: [p.pin],
                                                    },
                                                  }
                                                );
                                              }
                                              setProperties((prev) =>
                                                prev.filter((x) => x.pin !== p.pin)
                                              );
                                              setSelectedPins((prev) =>
                                                prev.filter((x) => x !== p.pin)
                                              );
                                            } catch (err) {
                                              alert(
                                                err.response?.data?.error ||
                                                "Failed to delete."
                                              );
                                            }
                                          },
                                        });
                                      }
                                    }}
                                    className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg transition border border-red-200"
                                  >
                                    🗑
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingProperty && (
        <EditModal
          property={editingProperty}
          inspectorId={selectedUser?.id}
          onClose={() => setEditingProperty(null)}
          onSaved={handlePropertySaved}
        />
      )}

      {/* Confirm Dialog */}
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
